import {User} from '../../services/src/core.service.authentication';
import {Logger, Event as LogEvent} from '../../support/src/core.support.logger';
import workerEntry from '../_workers/core.util.database.worker';
/**
 * Logging Origin
 */
const origin: string = "core.util.database";
/**
 * Database Key Type
 */
type DatabaseKey = string | number | Date | BufferSource | IDBKeyRange;
/**
 * Database `Document` Metadata
 */
interface DocumentMetadata {
    added: number,
    updated: number,
    table: string,
    database: string,
    userID?: string
}
/**
 * The set of Database operators.
 * 
 * - `<=` Find all records that are less then the
 *   provided value.
 * - `>=` Find all recors that are greater than
 *   the provided value.
 * - `not` Find all records that dont meet the
 *   provided value.
 * - `like` Find all records that are similar
 *   to the value. The similarity is 
 */
type Operators = 
 | "=="
 | "<="
 | ">="
 | "not"
 | "like";
/**
 * Unary Operators that join sets of queries to get complex requests
 */
type UnaryOperators =
| "AND"
| "OR";
/**
 * Offline database session interface
 */
interface Session {
    /**
     * List of registered observers
     */
    observers: Array<Observer>,
    /**
     * List of commited transactions
     */
    transactions: Array<TransactionEvent>
}
/**
 * Database transaction events
 */
type TransactionEvents =
| "create"
| "update"
| "delete";
/**
 * Database observer interface
 */
interface Observer {
    id: string,
    databaseID: string,
    tableID: string,
    documentID?: string,
    timestamp: number
}
/**
 * Database transaction interface
 */
interface TransactionEvent {
    document: any,
    transactionID: TransactionEvents,
    databaseID: string,
    tableID: string,
    timestamp: number,
    syncedObservers: Array<string>
}
/**
 * Document transaction commit interface
 */
 interface DocumentCommit {
    collection: Array<Document>,
    document: Document,
    timestamp: number,
    type: TransactionEvents
}
/**
 * `QueryCollection` Grouped Query Configuration
 */
interface GroupedQuery {
    keyPath: string,
    direction: "ASC" | "DESC"
}
/**
 * `QueryCollection` Ordered Query Configuration
 */
interface OrderedQuery {
    keyPath: string,
    direction: "ASC" | "DESC"
}
/**
 * Core Local Database Services
 */
export class Database {
    /**
     * Database name
     */
    readonly database: string;
    /**
     * Initilize database services.
     * 
     * Note: if indexedDB is not available on on
     * current device then a runtime exception will 
     * be thrown, make sure availability is checked
     * before creating a connection instance.
     * @example
     * ```javascript
     * var database = Database('relationships');
     * ...
     * ...
     * ```
     * @param database database name
     */
    constructor(
        database: string
    ) {
        // check database availability
        if (!Database.isAvailable) {
            throw new Error(logEvent("indexeddb-not-available"));
        }
        this.database = database;
    }
    /**
     * Get database table reffrence.
     * @param name Database table name
     */
    table(
        name: string
    ): TableReffrence {
        return new TableReffrence(name, this.database)
    }
    /**
     * Checkes if IndexedDB is available on browser
     */
    static get isAvailable() : boolean {
        if (('indexedDB' in window))
            return true;
        return false;
    }
}
/**
 * Database Query Library.
 */
export class Query {
    /**
     * Database name
     */
    readonly databaseName: string;
    /**
     * Database Connection Object
     */
    connectionRef: IDBDatabase | undefined;
    /**
     * Database table name
     */
    readonly tableName: string;
    /**
     * Index name
     */
    readonly indexName: string | undefined;
    /**
     * Identifies the `user lock` index
     */
    readonly userLockIndex: string = "cudd_userID";
    /**
     * Create query refference
     * @param index Index name
     * @param objectStore Database table refference
     */
    constructor(
        database: string,
        table: string,
        index?: string
    ) {
        this.tableName = table;
        this.indexName = index;
        this.databaseName = database;
    }
    /**
     * Database Connection refference. If a connection to the database
     * is not opend yet, it will attempt a connection request and store 
     * the `IDBDatabase` object for future connection requests.
     * @param refresh Identifies a that a new connection is to be attempted.
     */
    async connection(
        refresh: boolean = false
    ): Promise<IDBDatabase> {
        return new Promise(
            (resolve, reject) => {
                if (this.connectionRef !== undefined && !refresh) {
                    resolve(this.connectionRef);
                    return;
                }
                // open connection to database
                const request = indexedDB.open(this.databaseName);
                // handle connection errors
                request.onerror = () => {
                    reject(logEvent("database-connection-failed", this.databaseName));
                }
                // connection success
                request.onsuccess = () => {
                    request.result.onversionchange = () => {
                        // database version was updated close existing connections
                        if (this.connectionRef) {
                            // close connection
                            this.connectionRef?.close();
                            // reopen connection for further requests
                            this.connectionRef = undefined;
                            Logger.message(origin, 
                            `Database '${this.databaseName}' connection closed, because a versionchange event was trigered`);
                        }
                    }
                    // update database connection object for future connection requests
                    // in current instance
                    this.connectionRef = request.result;
                    resolve(request.result);
                }
            }
        )
    }
    /**
     * Get an upgraded database connection. This will reset the current connection aswell
     * to the newly created version. Only call this method if the database structure requires
     * an update.
     * Note: This function will only resolve with the upgraded connection once all existing 
     * connections have been closed.
     */
    async upgradededConnection(): Promise<IDBOpenDBRequest> {
        return new Promise(
            async (resolve, reject) => {
                // await closure of any existing connections
                this.connectionRef?.close();
                // open database with an incremented version
                const version = (await this.getDatabaseVersion())+1;
                // cycle through connection with an interval of 100 milisceonds
                let timeout = setInterval(() => {
                    // open a new instance to the database
                    const request = indexedDB.open(this.databaseName, version);
                    request.onblocked = () => {
                        // enable logging in development mode
                        reject(logEvent("connection-already-open", 
                        this.databaseName, this.tableName));
                    }
                    request.onupgradeneeded = () => {
                        // disable connection cycle
                        clearInterval(timeout);
                        // enable logging in development mode
                        Logger.message(origin, 
                        `Database '${this.databaseName}' upgrade connection successful`);
                        // return upgraded refference
                        resolve(request)
                    }
                    request.onerror = (event: any) => {
                        // disable connection cycle on error events
                        clearInterval(timeout);
                        // once an upgraded operation is completed it is manually closed
                        // this triggers an AbortError
                        if (event.target?.error?.name !== "AbortError") {
                            console.log(event);
                            // enable logging in development mode
                            reject(logEvent("operation-failed", 
                            this.databaseName, this.tableName, this.indexName));
                        }
                    }
                }, 300)
            }
        )
    }
    /**
     * Database objectstore refference
     * @param mode Identifies the transaction mode for the objectstore, by default
     * `readWrite` is set.
     */
    async objectStore(
        mode: "readwrite" | "readonly" = "readwrite"
    ): Promise<IDBObjectStore> {
        // fetch a new one
        const connection = await this.connection();
        // check if objectStore exists in database
        if (!connection.objectStoreNames.contains(this.tableName))
            // consider table not found as a fatal error
            throw new Error(logEvent("table-not-found", 
            this.databaseName, this.tableName));
        // open a transaction for table
        const transaction = connection
        .transaction([this.tableName], mode);
        const objectStore = transaction
        .objectStore(this.tableName);
        return objectStore;
    }
    /**
     * Get a objectstore or index refference for current connection.
     * An objectstore will be returned if an index refference isnt set.
     */
    async transaction(): Promise<IDBObjectStore | IDBIndex> {
        // get active objectStore
        const objectStore = await this.objectStore();
        // return polymorpic transaction instance
        return this.indexName 
        ? objectStore.index(this.indexName)
        : objectStore;
    }
    /**
     * Get the session instace
     */
    private get session(): Session { return JSON.parse(sessionStorage.getItem(origin) || "{}") }
    /**
     * Set the session isntace
     */
    private set session(session: Session) { sessionStorage.setItem(origin, JSON.stringify(session)) }
    /**
     * Add a observer to the commited transactions on the specified `scope`. The observer
     * will notify the `handler` when an `create`, `update` or `delete` transaction is
     * commited within the specified scope.
     * @param handler Function to call when transaction event is completed within the
     * query scope.
     * @param type Specific transaction to look for. By default `all` transaction
     * events are included.
     * @param scope Identidies the scope of the transactions to be notified.
     * @param documentID The provided document id if the `scope` is identified
     * as `document`.
     */
    onTransaction(
        handler: (
            transaction: TransactionEvent
        ) => {},
        type: TransactionEvents | "all" = "all",
        scope: "database" | "table" | "document",
        documentID?: string 
    ): Function {
        const session = this.session;
        const observers = session?.observers || [];
        //const worker = new Worker('core.util.database.worker.js');
        // load worker javascript file
        // Do something with your data
        //worker.onerror = (event) => { console.log(event) }
        //worker.onmessageerror = (event) => { console.log(event) }
        // register observer
        const activeObserver: Observer = {
            id: `TB-${Date.now()}`,
            databaseID: this.databaseName,
            tableID: this.tableName,
            documentID: documentID,
            timestamp: Date.now()
        }
        observers.push(activeObserver);
        this.session = {...session, observers: observers};
        Logger.message(origin, `Transaction observer attached on path `+
        `[${this.databaseName} / ${this.tableName}]`);
        // worker response handler
        const workerOnmessage = async (event: any) => {
            const transaction: TransactionEvent = event.data[0];
            const observer: Observer = event.data[1];
            // check if the observer was dismounted
            if (this.session.observers.findIndex(({id}) => id === observer.id) === -1) return;
            const ledger: Session = event.data[2];
            Logger.message(origin, `Transaction commited at [${transaction.databaseID} / `+
            `${transaction.tableID} / ${transaction.document.id} :: ${transaction.transactionID.toUpperCase()}]`);
            // inform handler
            console.log(this.session.observers.findIndex(({id}) => id === observer.id), this.session.observers, observer)
            handler(transaction);
            //console.log(ledger)
            // commit procesed by observer
            if (ledger) this.session = ledger;
        }
        // report observer initilization to worker
        workerEntry({data: [
            true, this.session, activeObserver, this.databaseName, 
            this.tableName, documentID, type, scope
        ]}, workerOnmessage);
        // worker.postMessage([
        //     true, JSON.parse(sessionStorage.getItem(origin) || "{}"),
        //     activeObserver, this.databaseName, this.tableName, 
        //     documentID, type, scope
        // ]);
        // process approved transactions
        // worker.onmessage = async (event) => {
        //     const transaction: TransactionEvent = event.data[0];
        //     const ledger: string = event.data[1];
        //     Logger.message(origin, `${transaction.transactionID} transaction commited at `+
        //     `${this.databaseName}/${this.tableName}/${transaction.documentID}`);
        //     // inform handler
        //     handler(transaction, await this.getAll());
        //     // commit procesed by observer
        //     if (ledger) sessionStorage.setItem(origin, ledger);
        // }
        // observer event loop
        let observer = setInterval(async () => {
            // report transaction to worker to evaluate valid transactions
            // and generate an updated ledger
            // worker.postMessage([
            //     false, JSON.parse(sessionStorage.getItem(origin) || "{}"), 
            //     activeObserver, this.databaseName, this.tableName, 
            //     documentID, type, scope
            // ])
            workerEntry({data: [
                false, this.session, activeObserver, this.databaseName, 
                this.tableName, documentID, type, scope
            ]}, workerOnmessage);
        }, 100);
        // transaction observer detach function
        return () => {
            Logger.message(origin, `Transaction observer detached from path `+
            `[${this.databaseName} / ${this.tableName}]`);
            // detach event loop
            clearInterval(observer);
            // detach commit worker
            //worker.terminate();
            // detach observer
            const session = this.session;
            const observers = session.observers;
            const index = observers.findIndex(({id}) => id === activeObserver.id);
            observers.splice(index, 1);
            this.session = {...session, observers: observers};
        }
    }
    /**
     * Get updated database version
     */
    async getDatabaseVersion(): Promise<number> {
        return new Promise(
            async (resolve, reject) => {
                resolve((await this.connection()).version);
            }
        )
    }
    /**
     * Identifies the table as `user locked`.
     * User locked tables only fetch and add
     * records specific to the current logged
     * in user. If table is locked and no user
     * is logged in, fetch and add requests will
     * fail.
     */
    async isUserLocked(): Promise<boolean> {
        return (await this.objectStore()).indexNames
            .contains(this.userLockIndex);
    }
    /**
     * Sets filters to records in case the table is `user locked`.
     * @param key value to search
     */
    private async filters(
        key: DatabaseKey
    ): Promise<DatabaseKey> {
        return new Promise(
            async (resolve, reject) => {
                if (await this.isUserLocked()) {
                    if (!User.uid) {
                        return reject(logEvent("no-authenticated-user"));
                    } else {
                        if (!key) resolve(User.uid);
                        return resolve(IDBKeyRange.bound(key, User.uid));
                    }
                } else return resolve(key);
            }
        )
    }
    /**
     * Checks if `user lock` critera's are met.
     * @param cursor Current cursor position
     */
    async lockTest(
        document: any
    ): Promise<boolean> {
        return new Promise(
            async (resolve, reject) => {
                // user lock filter
                if (await this.isUserLocked()) {
                    if (User.uid) {
                        if (document[this.userLockIndex] !== User.uid)
                            return resolve(false);
                    } else return reject(logEvent("no-authenticated-user"));
                }
                resolve(true);
            }
        )
    }
    /**
     * Log a transaction event for event listners.
     * Important: Transactions are only logged if there are 
     * active `Observers`, if there are no active `Observers`
     * the transaction wont be logged.
     * @param event Transaction event
     * @param documentID Document id
     */
    logTransactionEvent(
        event: TransactionEvents,
        document: Document
    ): void {
        const session = this.session;
        if (session.observers === undefined || session.observers.length === 0) return;
        const transactions: Array<TransactionEvent> = session.transactions || [];
        transactions.push({
            document: {
                ...document.data, 
                id: document.id, 
                cudd_metadata: document.metadata
            },
            transactionID: event,
            databaseID: this.databaseName,
            tableID: this.tableName,
            timestamp: Date.now(),
            syncedObservers: []
        });
        this.session = {...session, transactions: transactions};
    }
    /**
     * Retrieves the number of records matching the given 
     * key or key range in query.
     * @param key value to search
     */
    count(
        key?: DatabaseKey
    ): Promise<number> {
        return new Promise(
            async (resolve, reject) => {
                const transaction = (await this.transaction())
                .count(key ? await this.filters(key) : key);
                transaction.onerror = _ => {
                    reject(logEvent("operation-failed", 
                    this.databaseName, this.tableName, this.indexName));
                }
                transaction.onsuccess = _ => 
                    resolve(transaction.result)
            }
        )
    }
    /**
     * Retrieves the first record matching 
     * the given key or key range in query or undefined
     * if no records matched.
     * @param key value to search
     */
    get(
        key: DatabaseKey
    ): Promise<Document | undefined> {
        return new Promise(
            async (resolve, reject) => {
                const transaction = await this.transaction();
                const query = transaction
                .get(key ? await this.filters(key) : key);
                query.onerror = _ => 
                    reject(logEvent("operation-failed", 
                    this.databaseName, this.tableName, this.indexName));
                    query.onsuccess = async _ => {
                    if (!query.result) resolve(undefined)
                    resolve(new Document(query.result, transaction.keyPath, 
                        this.tableName,this.databaseName, this.indexName));
                }
            }
        )
    }
    /**
     * Retrieves all records.
     * if key is provided the result will be
     * an array of matching values.
     * @param key value to search
     * @param limit Limit of records
     */
    getAll(
        key?: DatabaseKey | null,
        limit?: number
    ):Promise<DocumentCollection> {
        return new Promise(
            async (resolve, reject) => {
                const transaction = await this.transaction();
                const query = transaction
                .getAll(key ? await this.filters(key) : key, limit);
                query.onerror = _ => 
                    reject(logEvent("operation-failed", 
                    this.databaseName, this.tableName, this.indexName));
                    query.onsuccess = async _ =>
                    resolve(new DocumentCollection(query.result, 
                        transaction.keyPath, this.tableName,
                        this.databaseName, this.indexName
                    ));
            }
        )
    }
    /**
     * Retrieves the keys of records matching the given 
     * key or key range in query
     * @param key value to search
     * @param limit Limit of records
     */
    getAllKeys(
        key?: DatabaseKey | null,
        limit?: number
    ): Promise<Array<IDBValidKey>> {
        return new Promise(
            async (resolve, reject) => {
                const transaction = (await this.transaction())
                .getAllKeys(key ? await this.filters(key) : key, limit);
                transaction.onerror = _ => 
                    reject(logEvent("operation-failed", 
                    this.databaseName, this.tableName, this.indexName));
                transaction.onsuccess = _ =>
                    resolve(transaction.result)
            }
        )
    }
    /**
     * Retrieves the key of the first record matching 
     * the given key or key range in query.
     * @param key value to search
     */
    getKey(
        key: DatabaseKey
    ): Promise<IDBValidKey | undefined> {
        return new Promise(
            async (resolve, reject) => {
                const transaction = (await this.transaction())
                .getKey(key ? await this.filters(key) : key);
                transaction.onerror = _ => 
                    reject(logEvent("operation-failed", 
                    this.databaseName, this.tableName, this.indexName));
                transaction.onsuccess = _ =>
                    resolve(transaction.result);
            }
        )
    }
}
/**
 * Query Collection Library
 */
class QueryCollection {
    /** 
     * IndexedDB Query Library
     */
    private readonly query: Query;
    /**
     * List of selected properties uptil current `QueryCollection` instance.
     */
    private readonly keyPaths: Array<string>;
    /**
     * List of selected operators uptil current `QueryCollection` instance.
     */
    private readonly operators: Array<Operators>;
    /**
     * List of provided values uptil current `QueryCollection` instance.
     */
    private readonly values: Array<any>;
    /**
     * List of unary operators uptil current `QueryCollection` instance.
     */
    private readonly unaryOperators: Array<UnaryOperators>;
    /**
     * Database Scope of the query
     */
    private readonly databaseName: string;
    /**
     * Database table scope of the query
     */
    private readonly tableName: string;
    /**
     * Database table index scope of the query
     */
    private readonly indexName?: string;
    /**
     * Records to limit the `DocumentCollection` by.
     */
    private readonly limitBy?: number;
    /**
     * Ordering configuration for the resulting `DocumentCollection`
     */
    private readonly orderBy?: OrderedQuery;
    /**
     * Grouping configuration for the resulting `DocumentCollection`
     */
    private readonly groupBy?: GroupedQuery;

    constructor(
        keyPaths: Array<string>,
        operators: Array<Operators>,
        values: Array<any>,
        unaryOperator: Array<UnaryOperators>,
        database: string,
        table: string,
        index?: string,
        limitBy?: number,
        orderBy?: OrderedQuery,
        groupBy?: GroupedQuery
    ) {
        // initilize database query library
        this.query = new Query(database, table, index);
        this.keyPaths = keyPaths;
        this.operators = operators;
        this.values = values;
        this.unaryOperators = unaryOperator;
        this.databaseName = database;
        this.tableName = table;
        this.indexName = index;
        this.limitBy = limitBy;
        this.orderBy = orderBy;
        this.groupBy = groupBy;
    }
    /**
     * Add a transaction listner to the query collection, this lisnter will trigger
     * the `handler` provided when a `Create`, `Update` or `Delete` transaction is 
     * completed within the scope of the query collection.
     * @param handler Function to call when transaction event is completed within the
     * query scope.
     * @param type Specific transaction to look for. By default `all` transaction
     * events are included.
     */
    onTransaction(
        handler: (document: DocumentCommit) => {},
        type: TransactionEvents | "all" = "all"
    ): Function {
        const observer = this.query.onTransaction(async (transaction) => {
            const objectStore = await this.query.objectStore();
            const collection = await this.get();
            // get the commited document
            const document = new Document(transaction.document, objectStore.keyPath,
            this.query.tableName, this.query.databaseName, this.query.indexName);
            // check if document passes query collection
            let passed: boolean = false;
            // check if record matches the query critera
            for (let queryIndex = 0; queryIndex < this.keyPaths.length; queryIndex++)
                // perform unary operator
                passed = this.performUnaryOperator(queryIndex, passed, document.data);
            // inform event if tests passed
            if (passed && this.query.lockTest(document.data)) {
                let documents = collection.getAll();
                // perform order operation on the resulting collection
                documents = this.performOrder(documents);
                // perform group operation on the resulting collection
                documents = this.performGrouping(documents);
                // inform event
                handler({
                    collection: documents,
                    document: document,
                    timestamp: transaction.timestamp,
                    type: transaction.transactionID
                });
            }
        }, type, "table");
        // return observer detacher function
        return observer;
    }
    /**
     * Order the resulting `DocumentCollection` by the specified 
     * `keyPath` and direction.
     * Note: Calling this method again will re-write the previous
     * order configuration.
     * @param keyPath Document keyPath to refference
     * @param direction Order direction
     */
    orderedBy(
        keyPath: string,
        direction: "ASC" | "DESC"
    ): QueryCollection {
        return new QueryCollection(this.keyPaths, this.operators, this.values, 
        this.unaryOperators, this.databaseName, this.tableName, this.indexName, this.limitBy,
        {keyPath: keyPath, direction: direction} as OrderedQuery, this.groupBy);
    }
    /**
     * Group the resulting `DocumentCollection` by the specified 
     * `keyPath` and direction.
     * Note: Calling this method again will re-write the previous
     * group configuration.
     * @param keyPath Document keyPath to refference
     * @param direction Order direction
     */
    groupedBy(
        keyPath: string,
        direction: "ASC" | "DESC"
    ): QueryCollection {
        return new QueryCollection(this.keyPaths, this.operators, this.values, 
        this.unaryOperators, this.databaseName, this.tableName, this.indexName, this.limitBy,
        this.orderBy, {keyPath: keyPath, direction: direction} as GroupedQuery);
    }
    /**
     * Limit number of `Document` returned by `QueryCollection`.
     * Note: Calling this method again will re-write the previous
     * limit configuration.
     * @param limit Number or records to limit the result by
     */
    limit(
        limit: number
    ): QueryCollection {
        return new QueryCollection(this.keyPaths, this.operators, this.values, 
        this.unaryOperators, this.databaseName, this.tableName, this.indexName, limit,
        this.orderBy, this.groupBy);
    }
    /**
     * Perform a unary `And` operation with the proceeding query.
     * @param keyPath Record property to use
     * @param operation Condition operation to perform
     * @param value Key value to filter with
     */
    and(
        keyPath: string,
        operator: Operators,
        value: any
    ): QueryCollection {
        // append the query to the current list
        this.keyPaths.push(keyPath);
        this.operators.push(operator);
        this.values.push(value);
        this.unaryOperators.push("AND");
        return new QueryCollection(this.keyPaths, this.operators, this.values, 
        this.unaryOperators, this.databaseName, this.tableName, this.indexName, this.limitBy,
        this.orderBy, this.groupBy);
    }
    /**
     * Perform a unary `OR` operation with the proceeding query.
     * By default `AND` is used as the unary operation between all quries.
     * @param keyPath Record property to use
     * @param operation Condition operation to perform
     * @param value Key value to filter with
     */
    or(
        keyPath: string,
        operator: Operators,
        value: any
    ): QueryCollection {
        // append the query to the current list
        this.keyPaths.push(keyPath);
        this.operators.push(operator);
        this.values.push(value);
        this.unaryOperators.push("OR");
        return new QueryCollection(this.keyPaths, this.operators, this.values, 
        this.unaryOperators, this.databaseName, this.tableName, this.indexName, 
        this.limitBy, this.orderBy, this.groupBy);
    }
    /**
     * Perform order operation on the current result records
     * for `get` operation and return new ordered collection.
     * @param configuration Order configuration for current
     * query collection.
     * @param collection Current result list
     */
    private performOrder(
        collection: Array<any>
    ): Array<any> {
        if (this.orderBy !== undefined) {
            const orderConfig = this.orderBy;
            // order the resulting document collection
            collection.sort((a,b) => {
                if (this.orderBy?.direction === "ASC")
                    return a[orderConfig.keyPath]-b[orderConfig.keyPath];
                return b[orderConfig.keyPath]-a[orderConfig.keyPath];
            })
        } 
        return collection;
    }
    /**
     * Perform grouping operation on the current result records
     * for `get` operation and return new grouped collection.
     * @param configuration Group configuration for current
     * query collection.
     * @param collection Current result list
     */
    private performGrouping(
        collection: Array<any>
    ): Array<any> {
        // TO DO: IM LAZY
        // ...............
        // ...............
        return collection;
    }
    /**
     * Perform query operator evaluation
     * @param operator Current Query Operator
     * @param document Current Document
     * @param keyPath Current Query KeyPath
     * @param value Curent Query Valur
     */
    private performOperator(
        operator: Operators,
        document: any,
        keyPath: string,
        value: any
    ): boolean {
        switch (operator) {
            case "<=":
                return document[keyPath] <= value;
            case "not":
                return document[keyPath] != value;
            case ">=":
                return document[keyPath] >= value;
            case "like":
                return `${document[keyPath]}`.search(value) > -1;
            default:
                // equals by default
                return document[keyPath] == value;
        }
    }
    /**
     * Perform Unary Operator Evaluation
     * @param index Current Document Index
     * @param prevOperatorPassed Previous Query Result
     * @param document Current Document
     */
    private performUnaryOperator(
        index: number,
        prevOperatorPassed: boolean,
        document: any,
    ): boolean {
        // evaluate document for the current query
        const currntOperatorPassed = this.performOperator(this.operators[index], 
            document, this.keyPaths[index], this.values[index]);
        // ignore unary operation for the first query
        if (index === 0) return currntOperatorPassed;
        const unaryOperator = this.unaryOperators[index];
        // perform unary operation
        if (unaryOperator === "OR" 
            // perform OR operation on current and next query evaluation
            && (prevOperatorPassed
            || currntOperatorPassed)) 
                return true;
        else if (unaryOperator === "AND"
            // perform AND operation on current and next query evaluation
            && (prevOperatorPassed 
            && currntOperatorPassed))
                return true;
        else return false;
    }
    /**
     * Execute Query and get the resulting `DocumentCollection`
     */
    get(): Promise<DocumentCollection> {
        return new Promise(
            async (resolve, reject) => {
                let collection: Array<any> = [];
                // open cursor to collection
                const objectStore = await this.query.objectStore();
                const transaction = await this.query.transaction();
                const cursor = transaction.openCursor(null, "next");
                cursor.onerror = _ => 
                    reject(logEvent("operation-failed", 
                    this.query.databaseName, this.query.tableName, this.query.indexName));
                cursor.onsuccess = async _ => {
                    const result = cursor.result;
                    if (!result || (this.limitBy && collection.length === this.limitBy))
                        return resolve(new DocumentCollection(collection,
                            objectStore.keyPath, this.tableName, this.databaseName));
                    // get current document
                    const document = result.value;
                    // get the result for the initial query
                    let passed: boolean = false;
                    // check if record matches the query critera
                    for (let queryIndex = 0; queryIndex < this.keyPaths.length; queryIndex++)
                        // perform unary operator
                        passed = this.performUnaryOperator(queryIndex, passed, document);
                    // append result collection if tests passed
                    if (passed && this.query.lockTest(document)) collection.push(document);
                    // perform order operation on the resulting collection
                    collection = this.performOrder(collection);
                    // perform group operation on the resulting collection
                    collection = this.performGrouping(collection);
                    // move to next record
                    result.continue();
                }
            }
        )
    }
}
/**
 * Database Table Refference
 */
class TableReffrence {
    /**
     * Database query library
     */
    private readonly query: Query;
    /**
     * Initilize database table.
     * 
     * Note: If database or table isnt available at this
     * point in time, a runtime exception will be thrown.
     * Make sure before creating a table instance that
     * the database is available.
     * @param database Database name
     * @param name Database table name
     * @param version Database version
     */
    constructor(
        name: string,
        database: string
    ) {
        // initialize query class
        this.query = new Query(database, name);
    }
    /**
     * Indicates that the table generates its own
     * numeric auto-incremented keys for each record
     */
    async isAutoIncremented(): Promise<boolean> 
    {return (await this.query.objectStore()).autoIncrement}
    /**
     * List of configured indexes for table
     */
    async indexNames(): Promise<DOMStringList> 
    {return (await this.query.objectStore()).indexNames}
    /**
     * Get the assigned primaryKey(s) for table
    */
    async primaryKeys(): Promise<string | Array<string>>
    {return (await this.query.objectStore()).keyPath}
    /**
     * Check if table exists
     */
    async exists(): Promise<boolean> {
        if ((await this.query.connection()).objectStoreNames.contains(this.query.tableName))
            return true;
        return false;
    }
    /**
     * Returns true if the table has no records and false otherwise.
     */
    async isEmpty(): Promise<boolean> { return await this.count() === 0 }
    /**
     * Get the total number or records in the table
     */
    async count(): Promise<number> { return this.query.count() }
    /**
     * Get all records in the table
     * @param limit Maximum number of records to get
     */
    async get(
        limit?: number
    ): Promise<DocumentCollection> {return this.query.getAll(undefined, limit)}
    /**
     * Get refference to an index.
     * 
     * Indexes are similar to table columns, and exist to
     * improve the search performance of queries. All
     * tables are required to have indexes that define
     * the table schema.
     * @param name Table index name. 
     */
    index(
        name: string
    ): IndexReffrence {
        return new IndexReffrence(name, this.query.tableName, this.query.databaseName);
    }
    /**
     * Get refference to a document in the table.
     * @param id Document ID
     */
    document(
        id: string | Array<string>
    ): DocumentRefference {
        return new DocumentRefference(this.query.databaseName, this.query.tableName, id);
    }
    /**
     * Creates a new table in database. 
     * @example
     * ```javascript
     * var database = new Database('relationships');
     * // create refference to table
     * var table = database.table('friends');
     * // create new table with `friendID` as
     * // primary key field and disable auto
     * // primary key generation.
     * await table.create('friendID', false);
     * ```
     * @param primaryKey Table primary key name.
     * @param autoIncrement Allow table to generate
     * its own numericly incremented primary keys for
     * each record. By Default this is set to `true`
     * @param userLock Set table as `user locked`.
     * By default all tables added to the database
     * will be `user locked`, which will 
     * only consider the current users records during
     * add and fetch requests. `user locked` tables
     * requires a logged in user to perform any 
     * operations. It is recommended to set tables as
     * `user locked`.
     */
    async create(
        primaryKey?: string | Array<string>,
        autoIncrement: boolean = true,
        userLock: boolean = true
    ) : Promise<void> {
        return new Promise(
            async (resolve, reject) => {
                // close any existing connection
                this.query.connectionRef?.close();
                // open a new instance to the database
                this.query.upgradededConnection().then(request => {
                    // check if table isnt already created
                    if (request.result.objectStoreNames.contains(this.query.tableName))
                        return reject(logEvent("table-already-exists", 
                        this.query.databaseName, this.query.tableName));
                    // create object store
                    const table = request.result.createObjectStore(
                        this.query.tableName, 
                        {keyPath: primaryKey || null,
                        autoIncrement: autoIncrement})
                    if (userLock) table.createIndex(this.query.userLockIndex, 
                        this.query.userLockIndex, {unique: false})
                    // enable logging in development mode
                    Logger.message(origin, 
                    `Table was commited at path [${this.query.databaseName} / ${this.query.tableName} :: CREATE]`);
                    // close connection
                    request.result.close();
                    return resolve();
                }, error => reject(error))
            }
        )
    }
    /**
     * Adds a new record in table with the given value in key.
     * @param document Record to add to table
     */
    async add(
        document: any
    ): Promise<Document> {
        return new Promise(
            async (resolve, reject) => {
                // check if the identifies table exists
                if (!(await this.exists()))
                    return reject(logEvent("table-not-found", 
                    this.query.databaseName, this.query.tableName));
                // objectStore refference
                const objectStore = (await this.query.objectStore());
                // validate document requirments
                if (!Document.validatePrimaryKeys(document, objectStore)
                    || !Document.validateMetadata(document))
                    return reject(logEvent("document-data-invalid", 
                    this.query.databaseName, this.query.tableName))
                // if table is user locked then check if an authenticated user is logged in
                if (await this.query.isUserLocked() && !User.uid) 
                    return reject(logEvent("no-authenticated-user"));
                // add document metadata
                const metadata: DocumentMetadata = {
                    added: new Date().getTime(),
                    updated: new Date().getTime(),
                    table: this.query.tableName,
                    database: this.query.databaseName
                }
                // append to new record
                document = {...document, cudd_metadata: metadata};
                // set user id if table is user locked.
                if (await this.query.isUserLocked()) document.cudd_userID = User.uid;
                const transaction = objectStore.add(document);
                transaction.onerror = _ => {
                    // enable logging in development mode
                    reject(logEvent("document-already-exists", 
                    this.query.databaseName, this.query.tableName));
                }
                transaction.onsuccess = async _ => {
                    this.query.logTransactionEvent("create", 
                    new Document(document, objectStore.keyPath, this.query.tableName,
                    this.query.databaseName, this.query.indexName));
                    // enable logging in development mode
                    Logger.message(origin, `Document was commited at path [${this.query.databaseName} / `
                    + `${this.query.tableName} / ${transaction.result.toString()} :: CREATE]`);
                    resolve(new Document(document, objectStore.keyPath, 
                        this.query.tableName, this.query.databaseName))
                }
            }
        )
    }
    /**
     * Delete table.
     */
    async delete(): Promise<void> {
        return new Promise(
            async (resolve, reject) => {
                // close any existing connection
                this.query.connectionRef?.close();
                // open a new instance to the database
                this.query.upgradededConnection().then(request => {
                    if (!request.result.objectStoreNames.contains(this.query.tableName))
                    return reject(logEvent("table-not-found", 
                    this.query.databaseName, this.query.tableName));
                    // create object store
                    request.result.deleteObjectStore(this.query.tableName);
                    // enable logging in development mode
                    Logger.message(origin, 
                    `Table was commited from path [${this.query.databaseName} / ${this.query.tableName} :: DELETE]`);
                    // close connection
                    request.result.close();
                    resolve();
                }, error => reject(error))
            }
        )
    }
    /**
     * Deletes all records in table.
     */
    async clear(): Promise<void> {
        return new Promise(
            async (resolve,reject) => {
                if (!(await this.exists()))
                    return reject(logEvent("table-not-found", 
                    this.query.databaseName, this.query.tableName));
                const request = (await this.query.objectStore()).clear();
                request.onerror = _ => {
                    // enable logging in development mode
                    reject(logEvent("operation-failed", 
                    this.query.databaseName, this.query.tableName, this.query.indexName));
                }
                request.onsuccess = _ => {
                    // enable logging in development mode
                    Logger.message(origin, 
                    `Table was commited at path [${this.query.databaseName} / ${this.query.tableName} :: CLEAR]`);
                    resolve()
                };
            }
        )
    }
    /**
     * Perform conditional opertaions for record filtering.
     * @param keyPath Record property to use
     * @param operation Condition operation to perform
     * @param value Key value to filter with
     */
    where(
        keyPath: string,
        operation: Operators,
        key: DatabaseKey
    ): QueryCollection {
        return new QueryCollection([keyPath], [operation], [key], 
        ["AND"],this.query.databaseName, this.query.tableName, this.query.indexName);
    }
    /**
     * Add a transaction observer to table, this observer will trigger
     * the `handler` provided when a `Create`, `Update` or `Delete` transaction is 
     * commited within the scope of the table.
     * 
     * Important: The observer is attached within the main thread of the application
     * thus to avoid performance bottle-necks limit the use of this method.
     * @param handler Function to call when transaction event is commited
     * @param type Specific transaction to look for. By default `all` transaction
     * events are included.
     */
     onTransaction(
        handler: (document: DocumentCommit) => {},
        type: TransactionEvents | "all" = "all"
    ): Function {
        const observer = this.query.onTransaction(async (transaction) => {
            const objectStore = await this.query.objectStore();
            const collection = await this.query.getAll();
            // get the commited document
            const document = new Document(transaction.document, objectStore.keyPath,
            this.query.tableName, this.query.databaseName, this.query.indexName);
            // inform event
            handler({
                collection: collection.getAll(),
                document: document,
                timestamp: transaction.timestamp,
                type: transaction.transactionID
            });
        }, type, "table");
        // return observer detacher function
        return observer;
    }
}
/**
 * Database Index Refference
 */
class IndexReffrence {
    /**
     * Database query library
     */
    private readonly query: Query;
    /**
     * Create table index instance
     * @param name Indes name.
     * @param tableName Table Name index belongs to
     * @param request Database connection request
     * @param connection Database connection
     */
     constructor(
        name: string,
        tableName: string,
        database: string
    ) {
        this.query = new Query(database, tableName, name);
        if (name === this.query.userLockIndex)
            throw new Error(logEvent("index-reserved", database, tableName, name));
    }
    /**
     * Index property
     */
    async keyPaths(): Promise<string | string[]> {
        return (await this.query.objectStore()).index(this.query.indexName as string).keyPath;
    }
    /**
     * Index has no repeated values
     */
    async isUnique(): Promise<boolean> {
        return (await this.query.objectStore()).index(this.query.indexName as string).unique;
    }
    /**
     * Check if index exists
     */
     async exists(): Promise<boolean> {
        if ((await this.query.objectStore()).indexNames.contains(this.query.tableName))
            return true;
        return false;
    }
    /**
     * Creates a new index in index. It creates a new field/column 
     * defining a new data point for each database record to contain.
     * @example
     * ```javascript
     * // create connection to database
     * var database = new Database('relationships');
     * // create instance to table
     * var table = database.table('friends');
     * // create new index for table
     * table.index('name').create('name', true);
     * ```
     * @param property Property to assosiate with index
     * @param unique Identifies that index does not contain
     * duplicated values. By default it is set to `false`
     */
    async create(
        property: string,
        unique: boolean = false
    ): Promise<void> {
        return new Promise(
            async (resolve, reject) => {
                // open a new instance to the database
                this.query.upgradededConnection().then(request => {
                    // define index's for object store
                    request.transaction?.objectStore(this.query.tableName)
                    .createIndex(this.query.indexName as string, property, {unique: unique});
                    // enable logging in development mode
                    Logger.message(origin, `Index was commited at path [${this.query.databaseName} / `+
                    `/ ${this.query.tableName} : ${this.query.indexName} :: CREATE]`);
                    // close connection
                    request.result.close();
                    return resolve();
                }, error => reject(error))
            }
        )
    }
    /**
     * Removes index from table.
     */
    async delete(): Promise<void> {
        return new Promise(
            async (resolve, reject) => {
                // open a new instance to the database
                this.query.upgradededConnection().then(request => {
                    // define index's for object store
                    request.transaction?.objectStore(this.query.tableName)
                    .deleteIndex(this.query.indexName as string);
                    // enable logging in development mode
                    Logger.message(origin, `Index was commited at path [${this.query.databaseName} / `+
                    `/ ${this.query.tableName} : ${this.query.indexName} :: DELETE]`);
                    // close connection
                    request.result.close();
                    return resolve();
                }, error => reject(error))
            }
        )
    }
    /**
     * Perform conditional opertaions for record filtering
     * on index.
     * @param operation Condition operation to perform
     * @param key Key value to filter with
     */
    where(
        keyPath: string,
        operation: Operators,
        key: DatabaseKey
    ): QueryCollection {
        return new QueryCollection([keyPath], [operation], [key], 
            ["AND"], this.query.databaseName, this.query.tableName, this.query.indexName);
    }
}
/**
 * Database Table Document Refference
 */
class DocumentRefference {
    /**
     * Database query library
     */
     private readonly query: Query;
    /**
     * Document primary key(s)
     */
    readonly primaryKey: string | Array<string>;
    /**
     * Document Refference
     * @param database Database name
     * @param tableName Table name
     * @param documentKey Document PrimaryKey
     */
    constructor(
        database: string,
        tableName: string,
        documentKey: string | Array<string>
    ) {
        this.query = new Query(database, tableName)
        this.primaryKey = documentKey;
    }
    /**
     * Check if the specified document exists in the table.
     */
    exists(): Promise<boolean> {
        return new Promise(
            async (resolve, reject) => {
                // check if objectstore exists
                const objectStore = await this.query.objectStore();
                const request = objectStore.get(this.primaryKey);
                request.onsuccess = () => {
                    if (request.result) return resolve(true);
                    return resolve(false);
                }
                request.onerror = () => {
                    reject(logEvent("operation-failed", 
                    this.query.databaseName, this.query.tableName));
                }
            }
        )
    }
    /**
     * Get the `Document` specified
     */
    get(): Promise<Document> {
        return new Promise(
            async (resolve, reject) => {
                const objectStore = await this.query.objectStore();
                const request = objectStore.get(this.primaryKey);
                request.onsuccess = () => {
                    if (request.result) 
                        return resolve(new Document(request.result, objectStore.keyPath, 
                            this.query.tableName, this.query.databaseName));
                    return reject(logEvent("document-not-found", 
                    this.query.databaseName, this.query.tableName, 
                    undefined, this.primaryKey as string));
                }
                request.onerror = () => {
                    reject(logEvent("operation-failed", 
                    this.query.databaseName, this.query.tableName));
                }
            }
        )
    }
    /**
     * Configures document data in preperation of update or create. This will
     * embed the document primaryKey if the table uses in-line keys and only
     * updated the fields in the document data in correspondence with the new
     * document data.
     * Note: This also updates the document `updated` metadata
     * @param document New document data
     * @param override Indicates that the current data has to be overidden
     */
    private getDocumentData(
        document: any,
        override: Boolean = false
    ): Promise<any> {
        return new Promise(
            async (resolve, reject) => {
                const objectStore = await this.query.objectStore();
                // generate document data
                let doc = null;
                // add document metadata
                const metadata: any = {
                    table: this.query.tableName,
                    database: this.query.databaseName,
                    updated: new Date().getTime()
                }
                // only override document field if document already exists
                if (!override && await this.exists()) {
                    const currentDocument = (await this.get());
                    // document is being updated, override fields
                    doc = {...currentDocument.data, ...document, 
                    // append metadata
                    cudd_metadata: currentDocument.metadata}
                } else {
                    metadata.created = new Date().getTime();
                    doc = {...document, cudd_metadata: metadata}
                }
                // set user id if table is user locked.
                if (await this.query.isUserLocked()) doc.cudd_userID = User.uid;
                // identify table primarykey critera
                if (objectStore.autoIncrement)
                    // table uses auto-incremented keys
                    resolve(doc);
                else {
                    // table uses in-line primaryKeys, embed the document primarykey.
                    // check if table primarykey set matches the provided primarykey set
                    if (typeof objectStore.keyPath !== typeof this.primaryKey)
                        return reject(logEvent("document-primarykey-invalid", this.query.databaseName, 
                        this.query.tableName, undefined, this.primaryKey as string));
                    // identify record (if already exists)
                    // check table primarykey critrea
                    if (typeof objectStore.keyPath === "string")
                        doc[objectStore.keyPath] = this.primaryKey;
                    // table uses inline-keys
                    else
                        // cycle through each primary key for table
                        for (let keyIndex = 0; keyIndex < objectStore.keyPath.length; keyIndex++)
                            // check if corresponding key is set
                            if (this.primaryKey[keyIndex])
                                doc[objectStore.keyPath[keyIndex]] = this.primaryKey[keyIndex];
                    resolve(doc);
                }
            }
        )
    }
    /**
     * Update / Create document. If the document does not exist it will be created
     * automatically with the privided primaryKey(s).
     * @param document Document Data
     */
    set(
        document: any
    ): Promise<Document> {
        return new Promise(
            async (resolve, reject) => {
                // perform document validation
                if (!Document.validateMetadata(document)
                    || !Document.validateUserLock(document))
                    return reject(logEvent("document-data-invalid", this.query.databaseName, 
                    this.query.tableName, undefined, this.primaryKey as string));
                // perform update
                let request: IDBRequest<IDBValidKey> = {} as IDBRequest<IDBValidKey>;
                // get configured document data
                const data = await this.getDocumentData(document);
                const existed = await this.exists();
                const objectStore = await this.query.objectStore();
                // identify table primarykey critera
                if (objectStore.autoIncrement)
                    // table uses auto-incremented keys
                    request = objectStore.put(data, this.primaryKey);
                else 
                    // get configured document data
                    request = objectStore.put(data);
                request.onsuccess = async () => {
                    const document = new Document(data, objectStore.keyPath, 
                    this.query.tableName, this.query.databaseName);
                    const type = existed ? "update" : "create";
                    // log transaction event
                    this.query.logTransactionEvent(type,document);
                    Logger.message(origin, `Document was commited at path [${this.query.databaseName} / `
                    + `${this.query.tableName} / ${this.primaryKey} :: ${type.toUpperCase()}]`);
                    resolve(document);
                }
                request.onerror = () => {
                    reject(logEvent("operation-failed", 
                    this.query.databaseName, this.query.tableName));
                }
            }
        )
    }
    /**
     * Update existing document. If the document already exists its data
     * will be overridden. To merge with existing data use `set`. A
     * `document-not-found` error will be thrown if the `Document` does
     * not exist.
     * @param document Document Data
     */
    update(
        document: any
    ): Promise<Document> {
        return new Promise(
            async (resolve, reject) => {
                // perform document validation
                if (!Document.validateMetadata(document)
                    || !Document.validateUserLock(document))
                    return reject(logEvent("document-data-invalid", this.query.databaseName, 
                    this.query.tableName, undefined, this.primaryKey as string));
                // perform update
                let request: IDBRequest<IDBValidKey> = {} as IDBRequest<IDBValidKey>;
                // get configured document data
                if (!await this.exists()) {
                    reject(logEvent("document-not-found", this.query.databaseName, 
                    this.query.tableName, this.primaryKey.toString()));
                    return;
                }
                const data = await this.getDocumentData(document, true);
                const objectStore = await this.query.objectStore();
                // identify table primarykey critera
                if (objectStore.autoIncrement)
                    // table uses auto-incremented keys
                    request = objectStore.put(data, this.primaryKey);
                else 
                    // get configured document data
                    request = objectStore.put(data);
                request.onsuccess = () => {
                    const record = new Document(data, objectStore.keyPath, 
                    this.query.tableName, this.query.databaseName)
                    // log transaction event
                    this.query.logTransactionEvent("update", record);
                    Logger.message(origin, `Document was commited at path [${this.query.databaseName} / `
                    + `${this.query.tableName} / ${this.primaryKey} :: UPDATE]`);
                    resolve(record);
                }
                request.onerror = () => {
                    reject(logEvent("operation-failed", 
                    this.query.databaseName, this.query.tableName));
                }
            }
        )
    }
    /**
     * Remove document.
     */
    delete(): Promise<void> {
        return new Promise(
            async (resolve, reject) => {
                if (!await this.exists())
                    return reject(logEvent("document-not-found", 
                    this.query.databaseName, this.query.tableName, undefined, this.primaryKey as string));
                // get configured document data
                const data = (await this.get()).data;
                const objectStore = await this.query.objectStore();
                const request = objectStore.delete(this.primaryKey)
                request.onsuccess = () => {
                    this.query.logTransactionEvent("delete",
                    new Document(data, objectStore.keyPath, 
                    this.query.tableName, this.query.databaseName, this.query.indexName));
                    Logger.message(origin, `Document was commited at path [${this.query.databaseName} / `
                    + `${this.query.tableName} / ${this.primaryKey} :: DELETE]`);
                    resolve();
                }
                request.onerror = () => {
                    reject(logEvent("operation-failed", 
                    this.query.databaseName, this.query.tableName));
                }
            }
        )
    }
    /**
     * Add a transaction listner to the `Document`, this lisnter will trigger
     * the `handler` provided when a `Create`, `Update` or `Delete` transaction is 
     * commited within the scope of the `Document`.
     * @param handler Function to call when transaction is commited
     * @param type Specific transaction to look for. By default `all` transaction
     * events are included.
     */
     onTransaction(
        handler: (document: DocumentCommit) => {},
        type: TransactionEvents | "all" = "all"
    ): Function {
        const observer = this.query.onTransaction(async (transaction) => {
            const objectStore = await this.query.objectStore();
            const collection = await this.query.getAll();
            // get the commited document
            const document = new Document(transaction.document, objectStore.keyPath,
            this.query.tableName, this.query.databaseName, this.query.indexName);
            // inform event
            handler({
                collection: collection.getAll(),
                document: document,
                timestamp: transaction.timestamp,
                type: transaction.transactionID
            });
        }, type, "document", this.primaryKey as string);
        // return observer detacher function
        return observer;
    }
}
/**
 * Document Collection Operations
 */
class DocumentCollection {
    /**
     * Collection of documents
     */
    private collection: Array<Document>;
    /**
     * Database name
     */
    private readonly databaseName: string;
    /**
     * Database objectStore refference
     */
    private readonly tableName: string;
    /**
     * Database table index refference
     */
    private readonly indexName?: string;
    /**
     * Table primary keyPath
     */
    private readonly keyPath: string | Array<string>;
    /**
     * Number of records in collection
     */
    readonly length: number;
    /**
     * Identifies if the `DocumentCollection` is 
     * empty.
     */
    readonly empty: boolean;
    /**
     * Create a new `DocumentCollection` instance
     * @param collection Document collection
     * @param objectStore Dataabse objectStore refference
     */
    constructor(
        collection: Array<any>,
        keyPath: string | Array<string>,
        tableName: string,
        databaseName: string,
        indexName?: string
    ) {
        this.tableName = tableName;
        this.indexName = indexName;
        this.keyPath = keyPath;
        this.databaseName = databaseName;
        this.collection = this.toDocuments(collection);
        this.length = collection.length;
        this.empty = collection.length === 0;
    }
    /**
     * Convet all records to `Document` objects and
     * returns an array of `Documents`.
     * @param collection Raw record object
     */
    private toDocuments(
        collection: Array<any>
    ): Array<Document> {
        let result: Array<Document> = [];
        for (const record of collection)
            result.push(
                new Document(record, this.keyPath, this.tableName, 
                    this.databaseName, this.indexName)
            )
        return result;
    }
    /**
     * Gets all documents in current collection.
     */
    getAll(): Array<Document> {return this.collection}
    /**
     * Sort objectstore or index result collection.
     * 
     * Note: only property values that are of `string` or
     * `number` will be succesfuly sorted all other types
     * will be ignored. if `Date` are required to be sorted
     * then make sure that the `Date` is converted to its
     * numeric format then stored.
     * @param order Record ordering critera
     * @param keyPath Record property to use for filtering, 
     * if index name is set, it will used as the keyPath.
     */
    orderBy(
        keyPath: string | number,
        order: "ASC" | "DESC"
    ): DocumentCollection {
        const isAsec = order === "ASC";
        this.collection.sort((a, b) => {
            // sort as numeric value
            if (typeof a.data[keyPath] === "number") {
                if (isAsec) return a.data[keyPath] - b.data[keyPath];
                return b.data[keyPath] - a.data[keyPath];
            }
            // sort as string value
            if (typeof a.data[keyPath] === "string") {
                const valueA = (a.data[keyPath] as string).toUpperCase();
                const valueB = (b.data[keyPath] as string).toUpperCase();
                if (isAsec && valueA < valueB) return -1;
                else if (isAsec && valueA > valueB) return 1;
                else if (!isAsec && valueB < valueA) return -1;
                else return 1;
            }
            // ignore all other types
            return 1;
        })
        return this;
    }
    /**
     * Remove all matching documents in current collection from
     * database.
     * @param keyPath Document property to compare
     * @param key Expected property value
     */
    batchDelete(
        keyPath?: string,
        key?: IDBValidKey
    ): Promise<void> {
        return new Promise(
            async (resolve, reject) => {
                for (const document of this.collection) {
                    await document.delete(keyPath, key)
                    .then(_ => resolve())
                    .catch(_ => reject(logEvent("operation-failed", 
                    this.databaseName, this.tableName, undefined, document.id)))
                }
            }
        )
    }
}
/**
 * Database Record
 */
class Document {

    private readonly query: Query;
    /**
     * Document record key. Identifies record in
     * database.
     */
    readonly id: DatabaseKey;
    /**
     * Document record data.
     */
    readonly data: any;
    /**
     * Document auto-generated matadata
     */
    readonly metadata: DocumentMetadata;
    /**
     * Database name of document
     */
    private readonly databaseName: string;
    /**
     * Database objectStore refference
     */
    private readonly tableName: string;
    /**
     * Table primary keyPath
     */
    private readonly keyPath: string | Array<string>;
    /**
     * Create a new document instance.
     * @param id Document id
     * @param data Document data
     * @param metadata Document metadata
     * @param objectStore Documents table refference
     */
    constructor(
        record: any,
        keyPath: string | Array<string>,
        tableName: string,
        databaseName: string,
        indexName?: string
    ) {
        this.query = new Query(databaseName, tableName, indexName);
        this.tableName = tableName;
        this.keyPath = keyPath;
        this.id = this.getID(record);
        this.metadata = this.getMetadata(record);
        this.data = this.getData(record);
        this.databaseName = databaseName;
    }
    /**
     * Extracts document id from record
     * @param record Database record
     */
    private getID(
        record: any
    ): DatabaseKey 
        {return record[this.keyPath as string]}
    /**
     * Extracts document data from record
     * @param record Database record
     */
    private getData(
        record: any
    ): any {
        const data = record;
        delete data.cudd_metadata;
        delete data.cudd_userID;
        return data;
    }
    /**
     * Extracts document metadata from record
     * @param record Database record
     */
    private getMetadata(
        record: any
    ): DocumentMetadata {
        return {...record.cudd_metadata, userID: record?.cudd_userID};
    }
    /**
     * Updates document record in database and returns
     * the new `Document`
     * @param data New document data
     */
    update(
        data: any
    ): Promise<Document> {
        return new Promise(
            async (resolve, reject) => {
                const objectStore = await this.query.objectStore();
                // make sure document metadata arent being overriden
                if (!Document.validateMetadata(data)
                    || !Document.validateUserLock(data))
                    reject(logEvent("document-data-invalid", 
                    this.databaseName, objectStore.name, undefined, this.id));
                const document = data;
                const metadata = this.metadata;
                metadata.updated = new Date().getTime();
                document.metadata = metadata;
                // update record
                const transaction = objectStore
                .put(document, this.id as IDBValidKey);
                transaction.onerror = _ => 
                    reject(logEvent("operation-failed", 
                    this.databaseName, objectStore.name, undefined, this.id));
                transaction.onsuccess = _ => 
                    resolve(new Document(document, this.keyPath, this.tableName, this.databaseName));
            }
        )
    }
    /**
     * Deletes document from database.
     * if record meets critera (if provided) the promise will resolve
     * with `true` and `false` otherwise.
     * @param keyPath Document property to use
     * @param key Property value to check
     */
    delete(
        keyPath?: string,
        key?: IDBValidKey
    ): Promise<boolean> {
        return new Promise(
            async (resolve, reject) => {
                const objectStore = await this.query.objectStore();
                let transaction: IDBRequest<undefined>;
                if (keyPath && key) {
                    if (this.data[keyPath] === key) {
                        transaction = objectStore
                        .delete(this.id);
                    } else return resolve(false);
                } else
                    transaction = objectStore.delete(this.id);
                transaction.onerror = _ => reject(logEvent("operation-failed", 
                this.databaseName, objectStore.name, undefined, this.id));
                transaction.onsuccess = _ => resolve(true);
            }
        )
    }
    /**
     * Verify if the document data identifies all table indexes
     * @param data Document data 
     * @param objectStore Database objectStore refference the document belongs to
     */
    static validateIndexes(
        data: any,
        objectStore: IDBObjectStore
    ): boolean {
        // validate indxes
        for (const index of objectStore.indexNames)
            if (!data[index])
                return false;
        return true;
    }
    /**
     * Verify if the document data identifies all primary keys set to table
     * @param data Document data 
     * @param objectStore Database objectStore refference the document belongs to
     */
    static validatePrimaryKeys(
        data: any,
        objectStore: IDBObjectStore
    ): boolean {
        // check if primary keys are set
        let keyPaths = objectStore.keyPath;
        keyPaths = typeof keyPaths === "string" ? [keyPaths] : keyPaths;
        if (!objectStore.autoIncrement) 
            for (const keyPath of keyPaths) 
                if (!data[keyPath])
                    return false;                       
        // all validations passed
        return true;
    }
    /**
     * Verify if the document data does not override document metadata
     * @param data Document data 
     */
    static validateMetadata(
        data: any        
    ): boolean {              
        // check for reserved properties
        if (data?.cudd_metadata) return false;
        // all validations passed
        return true;
    }
    /**
     * Verify if the document data does not override document metadata
     * @param data Document data 
     */
    static validateUserLock(
        data: any        
    ): boolean {              
        // check for reserved properties
        if (data?.cudd_userID) return false;
        // all validations passed
        return true;
    }
}
/**
 * Generate Error message for database operations.
 * @param code Error code
 */
function logEvent(
    code: "indexeddb-not-available" 
    | "database-connection-failed"
    | "no-authenticated-user"
    | "operation-failed"
    | "table-not-found"
    | "table-already-exists"
    | "index-not-found"
    | "index-reserved"
    | "property-reserved"
    | "primarykey-not-set"
    | "document-data-invalid"
    | "document-not-found"
    | "document-already-exists"
    | "connection-already-open"
    | "document-primarykey-invalid",
    database?: string,
    table?: string,
    index?: string,
    document?: DatabaseKey,
    property?: string
) {
    let payload: LogEvent = {
        origin: origin,
        code: code,
        description: ""
    }
    let path = "";
    if (database) path = `[${database}`;
    if (table) path += ` \\ ${table}`;
    if (index) path += ` \\ ${index}`;
    if (document) path += ` \\ ${document}`;
    if (property) path += ` : ${property}]`;
    switch (code) {
        case "indexeddb-not-available":
            payload.description = 
            "IndexedDB is not available on the currently "+
            "running browser, offline database is unavailable";
            break;
        case "database-connection-failed":
            payload.description = 
            `Connection to database ${database} failed`+
            "running browser, offline database is unavailable";
            break;
        case "no-authenticated-user":
            payload.description = 
            `User locked tables require an authenticated user `+
            "for database operations";
            break;
        case "operation-failed":
            payload.description = 
            `The requested operation on path ${path} failed unexpectedly `+
            "make sure the requested table or index exists and indexeddb is avilable "+
            "on this device.";
            break;
        case "table-not-found":
            payload.description = 
            `The table for the requested operation on path ${path} couldnt be found `+
            "make sure the requested table exists on the database before any operations are made.";
            break;
        case "table-already-exists":
            payload.description = 
            `The table for the requested operation on path ${path} already exists `+
            "make sure the requested table name is correct.";
            break;
        case "index-not-found":
            payload.description = 
            `The index for the requested operation on path ${path} couldnt be found `+
            "make sure the requested index exists on the table before any operations are made.";
            break;
        case "index-reserved":
            payload.description = 
            `The requested index on path ${path} can not be used, as the index name is reserved `+
            "user another index name for the specified path";
            break;
        case "property-reserved":
            payload.description = 
            `The requested property on path ${path} can not be used, as the property name is reserved `+
            "user another property name for the specified path";
            break;
        case "primarykey-not-set":
            payload.description = 
            `One of the primary keys for document ${path} is not set, the requested table was created with a primary key `+
            "identifier(s) for each record, set the primary key field(s) and try again";
            break;
        case "document-data-invalid":
            payload.description = 
            `The data specified for document does not meet all requirments, check if all primary keys are set `+
            "and the metadata property of the document isnt being overriden";
            break;
        case "document-not-found":
            payload.description = 
            `The requested document does not exist on path ${path}.`;
            break;
        case "document-already-exists":
            payload.description = 
            `The requested document on path ${path} already exists, request can not be completed.`;
            break;
        case "connection-already-open":
            payload.description = 
            `The database upgrade operation is being blocked by an existing open connection to the database at path ${path}, `+
            "either on the current or another window. Re-attempting upgrade connection.";
            break;
        case "document-primarykey-invalid":
            payload.description = 
            `The provided primarykey does not match the critrea set by table at ${path} make sure the table primarykey critrea `+
            "and the provided primaryKey match.";
            break;
        default:
            break;
    }
    Logger.error(payload);
    return code;
}